import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { ENV } from '../config/env';

const { FiUser, FiArrowRight, FiLoader, FiLock } = FiIcons;

const OnboardingPage = ({ authUser, onComplete }) => {
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleFinishOnboarding = async (e) => {
    e.preventDefault();
    if (username.length < 3) {
      setError("Username must be at least 3 characters.");
      return;
    }

    setLoading(true);
    setError('');
    try {
      const payload = {
        username: username,
        email: authUser.email,
        credit: 0,
        tier: "Free",
        user_id: authUser.id,
        created_at: new Date().toISOString().replace('T', ' ').split('.')[0],
        updated_at: new Date().toISOString().replace('T', ' ').split('.')[0]
      };

      const res = await fetch(
        `${ENV.NCB_DATA_API}/create/users?Instance=${ENV.NCB_INSTANCE}`,
        {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${ENV.NCB_SECRET_KEY}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify(payload)
        }
      );

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to initialize profile.");

      // Success - pass the full profile back to App
      onComplete({
        ...authUser,
        ...payload,
        profile_id: data.id
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-6">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-black rounded-[2rem] flex items-center justify-center mx-auto mb-6 shadow-2xl overflow-hidden">
            <img src="https://i.imgur.com/lBD1NkT.png" alt="DeepSearchz" className="w-full h-full object-cover" />
          </div>
          <h1 className="text-3xl font-black text-black tracking-tight mb-2">Claim Your Handle</h1>
          <p className="text-gray-400 text-[10px] font-black uppercase tracking-[0.2em]">Initialize your DeepSearchz identity</p>
        </div>

        <form onSubmit={handleFinishOnboarding} className="space-y-6">
          <div className="relative">
            <div className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400">
              <SafeIcon icon={FiUser} />
            </div>
            <input
              type="text"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/\s/g, ''))}
              className="w-full pl-14 pr-6 py-5 bg-gray-50 border-2 border-transparent rounded-[2rem] focus:border-black focus:bg-white transition-all text-sm font-bold outline-none"
              required
            />
          </div>

          {error && (
            <p className="text-red-500 text-[10px] font-black uppercase text-center tracking-widest">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-5 bg-black text-white rounded-[2rem] font-black text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-3 hover:bg-gray-900 transition-all shadow-xl disabled:opacity-50"
          >
            {loading ? <SafeIcon icon={FiLoader} className="animate-spin" /> : (
              <>
                Initialize Profile
                <SafeIcon icon={FiArrowRight} />
              </>
            )}
          </button>
        </form>

        <div className="mt-10 p-6 bg-amber-50 border-2 border-amber-200 rounded-[2rem]">
          <div className="flex items-start gap-3">
            <SafeIcon icon={FiLock} className="text-amber-600 mt-1 flex-shrink-0" />
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-amber-900 mb-2">
                Private Beta
              </p>
              <p className="text-[9px] text-amber-800 leading-relaxed">
                This application is currently in private beta. To access advanced features and higher credit tiers, please contact the owner for a premium plan upgrade.
              </p>
            </div>
          </div>
        </div>

        <p className="mt-8 text-center text-[8px] font-black text-gray-300 uppercase tracking-[0.3em]">
          Free tier: 0 Credits • Contact owner for upgrades
        </p>
      </motion.div>
    </div>
  );
};

export default OnboardingPage;